<template>
	<div class="flex flex-col my-1 p-3 rounded-lg shadow-sm border relative">
		<EditButton
			v-if="props.collectionMode?.editmode"
			class="absolute right-[5px] top-[10px]"
			:id="jsonData.id"
			type="podcast"
		/>
		<div class="flex items-center flex-1 mb-2">
			<div>
				<p class="text-primary font-semibold">{{ jsonData.mainOutcome.selectHeader}}</p>
				<h2 class="text-lg font-600 md:text-sm">{{ jsonData.mainOutcome.title }}</h2>
			</div>
		</div>
		<div class="audio-player flex flex-row items-center justify-between">
			<div class="flex items-center">
				<button
					@click="togglePlayback"
					class="hover:bg-success hover:text-white bg-[#4d6680] text-white font-bold w-[50px] h-[50px] rounded-full flex justify-center items-center border"
				>
					<EwIcon
						:icon="isPlaying ? 'tabler:player-pause' : 'tabler:player-play'"
						class="cursor-pointer w-[15px] h-[15px] md:text-base"
					/>
				</button>
				<div v-if="isLoading" class="soundPlayer-spinner">
					<div class="soundPlayer-spinner-border"></div>
				</div>
			</div>

			<div class="flex-grow md:mx-4 my-2 md:my-0 flex items-center justify-center md:justify-start">
				<p class="text-gray-400 font-semibold mr-4">{{ formatTime(currentTime) }}</p>
				<input
					type="range"
					min="0"
					:max="duration"
					v-model="currentTime"
					@input="seek"
					class="w-full appearance-none cursor-pointer h-1 rounded-full bg-gray-200 focus:outline-none soundPlayer-slider"
				/>
				<p class="text-gray-400 font-semibold ml-4 mr-2">{{ formatTime(duration) }}</p>
			</div>

			<div class="hidden md:flex items-center">
				<button
					@click="toggleMute"
					class="mr-4 hover:text-success-hover text-[#4d6680] font-bold w-[25px] h-[25px] rounded-full flex justify-center items-center"
				>
					<EwIcon :icon="isMuted ? 'tabler:volume-3' : 'tabler:volume-2'" class="cursor-pointer md:text-lg" />
				</button>
				<input
					type="range"
					min="0"
					max="100"
					v-model="volume"
					@input="setVolume"
					class="w-20 appearance-none cursor-pointer h-1 rounded-full bg-gray-200 focus:outline-none soundPlayer-slider"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, defineProps } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import EditButton from '../components/EditButton.vue';
import { useAppConfig } from '@/composables/useAppConfig';

import { Howl } from 'howler';
import type { configCollectionMode } from '@/types';

const props = defineProps<{

	modalVisibleAudio: boolean;
	jsonData: {
		id: string;
		mainOutcome:{
			title: string;
			selectHeader: string;
		}
		file_url: string;
	};
	collectionMode?: configCollectionMode | null;
}>();

const AppConfig = useAppConfig();
const { jsonData } = props;

const isPlaying = ref(false);
const volume = ref(50);
const currentTime = ref(0);
const duration = ref(0);
const isMuted = ref(false);
const isLoaded = ref(false);
const isLoading = ref(false);
const isUnloaded = ref(true);

let sound: Howl | null = null;

const createSound = () => {
	sound = new Howl({
		src: [jsonData.file_url],
		preload: true,

		volume: volume.value / 100,
		onplay: () => {
			isPlaying.value = true;
			isLoading.value = false; // Muzika ka filluar të luajë, ndalojmë ngarkimin
			updateCurrentTime();
		},
		onpause: () => {
			isPlaying.value = false;
		},
		onend: () => {
			isPlaying.value = false;
		},
		onseek: () => {
			currentTime.value = sound!.seek();
		},
		onload: () => {
			if (isLoading.value) {
				isLoading.value = false; // Ndalojmë ngarkimin kur muzika është gati
				sound!.play(); // Fillojmë të luajmë muzikën pas ngarkimit
			}
			duration.value = sound!.duration(); // Vendosim kohëzgjatjen e audios
		},
		onloaderror: (error: any) => {
			console.error('Error loading sound:', error);
		},
		onplayerror: (error: any) => {
			console.error('Error playing sound:', error);
		},
	});
};

const togglePlayback = () => {
	if (!sound) {
		isLoading.value = true;
		createSound();
	} else {
		isPlaying.value ? sound!.pause() : sound!.play();
	}
};
const seek = () => {
	if (sound) {
		sound.seek(currentTime.value);
	}
};

const setVolume = () => {
	if (sound) {
		sound.volume(isMuted.value ? 0 : volume.value / 100);
	}
};

const toggleMute = () => {
	isMuted.value = !isMuted.value;
	setVolume();
};

const formatTime = (timeInSeconds: number) => {
	const minutes = Math.floor(timeInSeconds / 60);
	const seconds = Math.floor(timeInSeconds % 60);
	return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

const updateCurrentTime = () => {
	const interval = setInterval(() => {
		if (isPlaying.value && sound) {
			currentTime.value = sound.seek();
		}
	}, 1000);

	onUnmounted(() => {
		clearInterval(interval);
	});
};

const stopAudio = () => {
	if (sound) {
		sound.stop();
	}
	isPlaying.value = false;
	currentTime.value = 0;
};

watch(
	() => props.modalVisibleAudio,
	(newVal) => {
		if (!newVal) {
			stopAudio();
		}
	}
);
watch(isPlaying, (newVal, oldVal) => {
	if (oldVal === false && newVal === true) {
		console.log('Muzika filloi të luajë!');
	}
});

onBeforeRouteLeave((to, from, next) => {
	stopAudio();
	next();
});
</script>

<style scoped>
.soundPlayer-slider {
	width: 100%;
	height: 2px;
	background-color: lightgray;
	cursor: pointer;
	border-radius: 1px;
	margin-right: 1rem;
}

.soundPlayer-spinner {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
}

.soundPlayer-spinner-border {
	border: 4px solid rgba(0, 0, 0, 0.1);
	border-left-color: #000;
	border-radius: 50%;
	width: 3rem;
	height: 3rem;
	animation: spin 1s infinite linear;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}
</style>
